import React from 'react'
import ATHeaderText from '../common/headerText/ATHeaderText'
import ATIconButton from '../common/formComponent/ATIconButton'
import { MdClose } from 'react-icons/md'
import * as Yup from 'yup'
import { Form, Formik } from 'formik'
import ATInput from '../common/formComponent/ATInput'
import { FormGroup } from 'react-bootstrap'
import ATTextButton from '../common/formComponent/ATTextButton'
import { useTranslation } from 'react-i18next'
import useCustomerDetails from '../../customHooks/customer/useCustomerDetails'
import { CustomerDetailData } from '../../interface/response/CustomerResponse'
import { setIntialValues } from './customerConstants'
import { UpdateCustomerRequest } from '../../interface/request/CustomerRequest'
import AddressForm from '../common/addressForm/AddressForm'
import { Address } from '../../interface/response/Address'
import { defaultAddressFormValue } from '../tenants/tenantConstants'

interface CustomerDetailsFormProps {
  isEdit: boolean
  onCloseModal: () => void
  data?: CustomerDetailData
}

const CustomerDetailsForm: React.FC<CustomerDetailsFormProps> = ({
  isEdit,
  onCloseModal,
  data,
}) => {
  const { t } = useTranslation(['translation'])
  const { addNewCustomer, updateCustomerInfo } = useCustomerDetails()

  const getInitialAddressFormValues = () => {
    if (!isEdit) {
      return defaultAddressFormValue
    }
    const initialValues: Address = {
      street: data?.address ?? '',
      city: data?.city ?? '',
      state: data?.state ?? '',
      country: data?.country ?? '',
      zipCode: `${data?.zipCode ?? ''}`,
      fullAddress: data?.address ?? '',
    }
    return initialValues
  }

  return (
    <div className="p-2">
      <ATHeaderText title={isEdit ? t('customer.edit') : t('customer.add')}>
        <ATIconButton
          icon={<MdClose size={16} className="text-dark" />}
          onClick={onCloseModal}
          variant="link"
        />
      </ATHeaderText>
      <div className="p-2">
        <Formik
          validateOnMount={true}
          enableReinitialize
          initialValues={setIntialValues(data)}
          validationSchema={Yup.object({
            customerName: Yup.string().required(t('form.required')),
            // state: Yup.string().required(t('form.required')),
            // city: Yup.string().required(t('form.required')),
            // zipCode: Yup.string().required(t('form.required')),
            salesRep: Yup.string().nullable(),
            accountManager: Yup.string().nullable(),
            contactNumber: Yup.string()
              .nullable()
              .matches(/^[0-9*()+ ]+$/, t('form.phonenumber')),
            // country: Yup.string().required(t('form.required')),
            // address: Yup.string().required(t('form.required')),
          })}
          onSubmit={(values) => {
            if (isEdit) {
              const updateRequest: UpdateCustomerRequest = {
                ...values,
                customerCode: data?.customerCode,
              }
              updateCustomerInfo(updateRequest)
            } else {
              addNewCustomer(values)
            }
            onCloseModal()
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isValid,
          }) => (
            <Form onSubmit={handleSubmit}>
              <div className="row">
                <FormGroup className="col-12 px-4">
                  <ATInput
                    label={t('customer.name')}
                    name="customerName"
                    onChangeHandler={handleChange}
                    onBlurHandler={handleBlur}
                    value={values.customerName}
                    isError={errors.customerName && touched.customerName}
                    error={errors.customerName}
                    placeholder=""
                  />
                </FormGroup>
                <FormGroup className="col-12">
                  <AddressForm
                    onChangeAddress={(address: Address) => {
                      setFieldValue('address', address?.street)
                      setFieldValue('state', address?.state)
                      setFieldValue('country', address?.country)
                      setFieldValue('city', address?.city)
                      setFieldValue('zipCode', address?.zipCode)
                    }}
                    initialValues={getInitialAddressFormValues()}
                  />
                </FormGroup>
                <FormGroup className="col-12 px-4">
                  <ATInput
                    label={t('customer.contacts')}
                    name="contactNumber"
                    onChangeHandler={handleChange}
                    onBlurHandler={handleBlur}
                    value={values.contactNumber}
                    isError={errors.contactNumber && touched.contactNumber}
                    error={errors.contactNumber}
                    placeholder=""
                    isMandatory={false}
                  />
                </FormGroup>

                <FormGroup className="col-6 px-4">
                  <ATInput
                    label={t('customer.account_manager')}
                    name="accountManager"
                    onChangeHandler={handleChange}
                    onBlurHandler={handleBlur}
                    value={values.accountManager}
                    isError={errors.accountManager && touched.accountManager}
                    error={errors.accountManager}
                    placeholder=""
                    isMandatory={false}
                  />
                </FormGroup>
                <FormGroup className="col-6 px-4">
                  <ATInput
                    label={t('customer.sales_rep')}
                    name="salesRep"
                    onChangeHandler={handleChange}
                    onBlurHandler={handleBlur}
                    value={values.salesRep}
                    isError={errors.salesRep && touched.salesRep}
                    error={errors.salesRep}
                    placeholder=""
                    isMandatory={false}
                  />
                </FormGroup>
              </div>
              <div className="d-flex py-3 px-2 justify-content-end">
                <div>
                  <ATTextButton
                    title={t('common.cancel')}
                    onPressHandler={onCloseModal}
                    isFilledButton={false}
                  />
                </div>
                <div>
                  <ATTextButton
                    title={isEdit ? t('common.update') : t('common.add')}
                    onPressHandler={handleSubmit}
                    disabled={!isValid}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}

export default CustomerDetailsForm
